<template>
    <div class="flex-1 bg-gray-50 p-6">
        <div class="max-w-4xl mx-auto">
            <!-- 页面标题 -->
            <div class="mb-8">
                <div class="flex items-center justify-between mb-4">
                    <div>
                        <h1 class="text-3xl font-bold text-gray-900 mb-2">用户管理</h1>
                        <p class="text-gray-600">管理系统用户信息和权限</p>
                    </div>
                    <button 
                        @click="goBackToChat"
                        class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 flex items-center space-x-2"
                    >
                        <i class="fas fa-arrow-left"></i>
                        <span>返回对话</span>
                    </button>
                </div>
            </div>

            <!-- 当前用户信息卡片 -->
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <div class="flex items-center justify-between mb-4">
                    <h2 class="text-xl font-semibold text-gray-900">当前用户信息</h2>
                    <div class="flex space-x-2">
                        <button 
                            @click="editCurrentUser"
                            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                        >
                            <i class="fas fa-edit"></i>
                            <span>编辑信息</span>
                        </button>
                    </div>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div class="space-y-4">
                        <div class="flex items-center space-x-3">
                            <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                <i class="fas fa-user text-white"></i>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">用户名</p>
                                <p class="font-medium text-gray-900">{{ currentUser.displayName }}</p>
                            </div>
                        </div>
                        
                        <div class="flex items-center space-x-3">
                            <div class="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
                                <i class="fas fa-at text-white"></i>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">登录账号</p>
                                <p class="font-medium text-gray-900">{{ currentUser.loginAccount }}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="space-y-4">
                        <div class="flex items-center space-x-3">
                            <div class="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                                <i class="fas fa-phone text-white"></i>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">手机号</p>
                                <p class="font-medium text-gray-900">{{ currentUser.phone }}</p>
                            </div>
                        </div>
                        
                        <div class="flex items-center space-x-3">
                            <div class="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
                                <i class="fas fa-shield-alt text-white"></i>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">用户角色</p>
                                <div class="flex items-center space-x-2">
                                    <span class="px-2 py-1 text-xs font-medium rounded-full" 
                                          :class="getRoleClass(currentUser.role)">
                                        {{ getRoleDisplayName(currentUser.role) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="mt-6 pt-6 border-t border-gray-200">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div class="text-center">
                            <p class="text-2xl font-bold text-blue-600">{{ formatDateTime(currentUser.createTime) }}</p>
                            <p class="text-sm text-gray-500">注册时间</p>
                        </div>
                        <div class="text-center">
                            <p class="text-2xl font-bold text-green-600">{{ formatDateTime(currentUser.lastLoginTime) || '从未登录' }}</p>
                            <p class="text-sm text-gray-500">最后登录</p>
                        </div>
                        <div class="text-center">
                            <p class="text-2xl font-bold text-purple-600">{{ currentUser.status === 1 ? '正常' : '禁用' }}</p>
                            <p class="text-sm text-gray-500">账户状态</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 用户列表（管理员和用户管理员可见） -->
            <div v-if="currentUser.role === 'admin' || currentUser.role === 'user_manager'" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div class="flex items-center justify-between mb-6">
                    <h2 class="text-xl font-semibold text-gray-900">用户列表</h2>
                    <!-- 只有管理员可以看到这些操作按钮 -->
                    <div v-if="currentUser.role === 'admin'" class="flex space-x-3">
                        <button 
                            @click="downloadExcelTemplate"
                            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                        >
                            <i class="fas fa-download"></i>
                            <span>下载模板</span>
                        </button>
                        <button 
                            @click="showExcelImportModal = true"
                            class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 flex items-center space-x-2"
                        >
                            <i class="fas fa-file-excel"></i>
                            <span>导入Excel</span>
                        </button>
                        <button 
                            @click="showAddUserModal = true"
                            class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center space-x-2"
                        >
                            <i class="fas fa-plus"></i>
                            <span>添加用户</span>
                        </button>
                    </div>
                </div>
                
                <!-- 搜索和筛选 -->
                <div class="flex flex-col sm:flex-row gap-4 mb-6">
                    <div class="flex-1">
                        <input 
                            v-model="searchQuery"
                            type="text"
                            placeholder="搜索用户名、账号或手机号"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <select 
                        v-model="statusFilter"
                        class="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="">全部状态</option>
                        <option value="1">正常</option>
                        <option value="0">禁用</option>
                    </select>
                    <select 
                        v-model="roleFilter"
                        class="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="">全部角色</option>
                        <!-- 管理员可以看到所有角色选项 -->
                        <template v-if="currentUser.role === 'admin'">
                            <option value="admin">管理员</option>
                            <option value="user_manager">用户管理员</option>
                            <option value="user">普通用户</option>
                        </template>
                        <!-- 用户管理员只能看到普通用户选项 -->
                        <template v-else-if="currentUser.role === 'user_manager'">
                            <option value="user">普通用户</option>
                        </template>
                    </select>
                </div>
                
                <!-- 用户表格 -->
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">用户信息</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">角色</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">注册时间</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-for="user in filteredUsers" :key="user.id" class="hover:bg-gray-50">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                            <i class="fas fa-user text-white"></i>
                                        </div>
                                        <div class="ml-4">
                                            <div class="text-sm font-medium text-gray-900">{{ user.displayName }}</div>
                                            <div class="text-sm text-gray-500">{{ user.loginAccount }}</div>
                                            <div class="text-sm text-gray-500">{{ user.phone }}</div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span class="px-2 py-1 text-xs font-medium rounded-full" 
                                          :class="getRoleClass(user.role)">
                                        {{ getRoleDisplayName(user.role) }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span class="px-2 py-1 text-xs font-medium rounded-full" 
                                          :class="user.status === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                                        {{ user.status === 1 ? '正常' : '禁用' }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ formatDate(user.createTime) }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div class="flex space-x-2">
                                        <button 
                                            @click="editUser(user)"
                                            class="text-blue-600 hover:text-blue-900"
                                            title="编辑用户"
                                        >
                                            <i class="fas fa-edit"></i>
                                        </button>
                                        <button 
                                            @click="openPermissionModal(user)"
                                            class="text-purple-600 hover:text-purple-900"
                                            title="权限管理"
                                        >
                                            <i class="fas fa-key"></i>
                                        </button>
                                        <button 
                                            v-if="user.id !== currentUser.id"
                                            @click="openResetPasswordModal(user)"
                                            class="text-orange-600 hover:text-orange-900"
                                            title="重置密码"
                                        >
                                            <i class="fas fa-unlock-alt"></i>
                                        </button>
                                        <button 
                                            @click="toggleUserStatus(user)"
                                            :class="user.status === 1 ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'"
                                            :title="user.status === 1 ? '禁用用户' : '启用用户'"
                                        >
                                            <i :class="user.status === 1 ? 'fas fa-ban' : 'fas fa-check'"></i>
                                        </button>
                                        <button 
                                            v-if="user.id !== currentUser.id"
                                            @click="deleteUser(user)"
                                            class="text-red-600 hover:text-red-900"
                                            title="删除用户"
                                        >
                                            <i class="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                
                <!-- 分页 -->
                <div class="mt-6 flex items-center justify-between">
                    <div class="text-sm text-gray-700">
                        显示第 {{ (currentPage - 1) * pageSize + 1 }} 到 {{ Math.min(currentPage * pageSize, totalUsers) }} 条，共 {{ totalUsers }} 条记录
                    </div>
                    <div class="flex space-x-2">
                        <button 
                            @click="prevPage"
                            :disabled="currentPage === 1"
                            class="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            上一页
                        </button>
                        <span class="px-3 py-2 text-gray-700">{{ currentPage }} / {{ totalPages }}</span>
                        <button 
                            @click="nextPage"
                            :disabled="currentPage === totalPages"
                            class="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            下一页
                        </button>
                    </div>
                </div>
            </div>

            <!-- 编辑用户弹窗 -->
            <div v-if="showEditModal" class="fixed inset-0 flex items-center justify-center z-50">
                <div class="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
                <div class="relative bg-white rounded-2xl p-8 w-[500px] max-w-[90vw] shadow-2xl">
                    <h3 class="text-2xl font-bold text-gray-900 mb-6">编辑用户信息</h3>
                    
                    <form @submit.prevent="saveUser" class="space-y-4">
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">显示名称</label>
                            <input 
                                v-model="editingUser.displayName"
                                type="text"
                                disabled
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                            />
                            <p class="text-xs text-gray-500 mt-1">显示名称不可修改</p>
                        </div>
                        
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">登录账号</label>
                            <input 
                                v-model="editingUser.loginAccount"
                                type="text"
                                disabled
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                            />
                            <p class="text-xs text-gray-500 mt-1">登录账号不可修改</p>
                        </div>
                        
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">手机号</label>
                            <input 
                                v-model="editingUser.phone"
                                type="tel"
                                disabled
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                            />
                            <p class="text-xs text-gray-500 mt-1">手机号不可修改</p>
                        </div>
                        
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">用户角色</label>
                            <select 
                                v-model="editingUser.role"
                                :disabled="currentUser.role !== 'admin'"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                                <option value="user">普通用户</option>
                                <option value="user_manager">用户管理员</option>
                                <option value="admin">管理员</option>
                            </select>
                            <p v-if="currentUser.role !== 'admin'" class="text-xs text-gray-500 mt-1">只有管理员可以修改用户角色</p>
                        </div>
                        
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">账户状态</label>
                            <select 
                                v-model="editingUser.status"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option :value="1">正常</option>
                                <option :value="0">禁用</option>
                            </select>
                        </div>
                        
                        <div class="flex space-x-4 pt-4">
                            <button 
                                type="submit"
                                class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                            >
                                保存
                            </button>
                            <button 
                                type="button"
                                @click="closeEditModal"
                                class="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                            >
                                取消
                            </button>
                        </div>
                    </form>
                    
                    <button 
                        @click="closeEditModal"
                        class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
                    >
                        <i class="fas fa-times text-gray-600"></i>
                    </button>
                </div>
            </div>

            <!-- 添加用户弹窗 -->
            <div v-if="showAddUserModal" class="fixed inset-0 flex items-center justify-center z-50">
                <div class="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
                <div class="relative bg-white rounded-2xl p-8 w-[500px] max-w-[90vw] shadow-2xl">
                    <h3 class="text-2xl font-bold text-gray-900 mb-6">添加新用户</h3>
                    
                    <form @submit.prevent="addUser" class="space-y-4">
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">显示名称</label>
                            <input 
                                v-model="newUser.displayName"
                                type="text"
                                required
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">登录账号</label>
                            <input 
                                v-model="newUser.loginAccount"
                                type="text"
                                required
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">密码</label>
                            <input 
                                v-model="newUser.password"
                                type="password"
                                required
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">手机号</label>
                            <input 
                                v-model="newUser.phone"
                                type="tel"
                                required
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-2">用户角色</label>
                            <select 
                                v-model="newUser.role"
                                :disabled="currentUser.role !== 'admin'"
                                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                                <option value="user">普通用户</option>
                                <option value="user_manager">用户管理员</option>
                                <option value="admin">管理员</option>
                            </select>
                            <p v-if="currentUser.role !== 'admin'" class="text-xs text-gray-500 mt-1">用户管理员只能创建普通用户</p>
                        </div>
                        
                        <div class="flex space-x-4 pt-4">
                            <button 
                                type="submit"
                                class="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                            >
                                添加用户
                            </button>
                            <button 
                                type="button"
                                @click="closeAddUserModal"
                                class="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                            >
                                取消
                            </button>
                        </div>
                    </form>
                    
                    <button 
                        @click="closeAddUserModal"
                        class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
                    >
                        <i class="fas fa-times text-gray-600"></i>
                    </button>
                </div>
            </div>

            <!-- 权限管理弹窗 -->
            <div v-if="showPermissionModal" class="fixed inset-0 flex items-center justify-center z-50">
                <div class="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
                <div class="relative bg-white rounded-2xl p-8 w-[500px] max-w-[90vw] shadow-2xl">
                    <h3 class="text-2xl font-bold text-gray-900 mb-6">用户权限管理</h3>
                    
                    <div class="mb-6">
                        <div class="flex items-center space-x-3 mb-4">
                            <div class="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                <i class="fas fa-user text-white"></i>
                            </div>
                            <div>
                                <p class="text-lg font-semibold text-gray-900">{{ permissionUser.displayName }}</p>
                                <p class="text-sm text-gray-500">{{ permissionUser.loginAccount }}</p>
                            </div>
                        </div>
                    </div>
                    
                    <form @submit.prevent="savePermissions" class="space-y-6">
                        <div class="space-y-4">
                            <h4 class="text-lg font-semibold text-gray-800">模块访问权限</h4>
                            
                            <div class="space-y-3">
                                <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div class="flex items-center space-x-3">
                                        <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                            <i class="fas fa-robot text-green-600"></i>
                                        </div>
                                        <div>
                                            <p class="font-medium text-gray-900">小助手</p>
                                            <p class="text-sm text-gray-500">AI聊天对话功能</p>
                                        </div>
                                    </div>
                                    <div class="flex items-center space-x-2">
                                        <span class="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">默认权限</span>
                                    </div>
                                </div>
                                
                                <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div class="flex items-center space-x-3">
                                        <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                            <i class="fas fa-history text-blue-600"></i>
                                        </div>
                                        <div>
                                            <p class="font-medium text-gray-900">历史记录</p>
                                            <p class="text-sm text-gray-500">查看聊天历史记录</p>
                                        </div>
                                    </div>
                                    <div class="flex items-center space-x-2">
                                        <span class="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">默认权限</span>
                                    </div>
                                </div>
                                
                                <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div class="flex items-center space-x-3">
                                        <div class="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                                            <i class="fas fa-star text-yellow-600"></i>
                                        </div>
                                        <div>
                                            <p class="font-medium text-gray-900">收藏夹</p>
                                            <p class="text-sm text-gray-500">收藏和管理重要对话</p>
                                        </div>
                                    </div>
                                    <div class="flex items-center space-x-2">
                                        <span class="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">默认权限</span>
                                    </div>
                                </div>
                                
                                <div class="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                                    <div class="flex items-center space-x-3">
                                        <div class="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                            <i class="fas fa-book text-purple-600"></i>
                                        </div>
                                        <div>
                                            <p class="font-medium text-gray-900">知识库</p>
                                            <p class="text-sm text-gray-500">访问知识库管理功能</p>
                                        </div>
                                    </div>
                                    <div class="flex items-center space-x-2">
                                        <label class="relative inline-flex items-center cursor-pointer">
                                            <input 
                                                v-model="permissionUser.knowledgeAccess"
                                                type="checkbox"
                                                class="sr-only peer"
                                            />
                                            <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>
                                </div>
                                
                                <!-- 只有管理员可以看到用户管理权限设置 -->
                                <div v-if="currentUser.role === 'admin'" class="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                                    <div class="flex items-center space-x-3">
                                        <div class="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                                            <i class="fas fa-users text-red-600"></i>
                                        </div>
                                        <div>
                                            <p class="font-medium text-gray-900">用户管理</p>
                                            <p class="text-sm text-gray-500">管理系统用户和权限</p>
                                        </div>
                                    </div>
                                    <div class="flex items-center space-x-2">
                                        <label class="relative inline-flex items-center cursor-pointer">
                                            <input 
                                                v-model="permissionUser.userManagementAccess"
                                                type="checkbox"
                                                class="sr-only peer"
                                            />
                                            <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="flex space-x-4 pt-4">
                            <button 
                                type="submit"
                                class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                            >
                                保存权限
                            </button>
                            <button 
                                type="button"
                                @click="closePermissionModal"
                                class="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                            >
                                取消
                            </button>
                        </div>
                    </form>
                    
                    <button 
                        @click="closePermissionModal"
                        class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
                    >
                        <i class="fas fa-times text-gray-600"></i>
                    </button>
                </div>
            </div>

            <!-- 重置密码确认弹窗 -->
            <div v-if="showResetPasswordModal" class="fixed inset-0 flex items-center justify-center z-50">
                <div class="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
                <div class="relative bg-white rounded-2xl p-8 w-[500px] max-w-[90vw] shadow-2xl">
                    <h3 class="text-2xl font-bold text-gray-900 mb-6">重置用户密码</h3>
                    
                    <div class="mb-6">
                        <div class="flex items-center space-x-3 mb-4">
                            <div class="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                                <i class="fas fa-unlock-alt text-white"></i>
                            </div>
                            <div>
                                <p class="text-lg font-semibold text-gray-900">{{ resetPasswordUser.displayName }}</p>
                                <p class="text-sm text-gray-500">{{ resetPasswordUser.loginAccount }}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                        <div class="flex items-start space-x-3">
                            <div class="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <i class="fas fa-exclamation-triangle text-yellow-600 text-sm"></i>
                            </div>
                            <div>
                                <h4 class="text-sm font-semibold text-yellow-800 mb-1">重要提示</h4>
                                <p class="text-sm text-yellow-700">
                                    确定要重置该用户的密码吗？密码将被重置为 <span class="font-mono font-bold bg-yellow-100 px-2 py-1 rounded">123456</span>，
                                    用户下次登录时需要使用新密码。
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="flex space-x-4">
                        <button 
                            @click="confirmResetPassword"
                            :disabled="isResettingPassword"
                            class="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                        >
                            <i v-if="isResettingPassword" class="fas fa-spinner fa-spin"></i>
                            <i v-else class="fas fa-unlock-alt"></i>
                            <span>{{ isResettingPassword ? '重置中...' : '确认重置' }}</span>
                        </button>
                        <button 
                            @click="closeResetPasswordModal"
                            class="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                        >
                            取消
                        </button>
                    </div>
                    
                    <button 
                        @click="closeResetPasswordModal"
                        class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
                    >
                        <i class="fas fa-times text-gray-600"></i>
                    </button>
                </div>
            </div>

            <!-- Excel导入弹窗 -->
            <div v-if="showExcelImportModal" class="fixed inset-0 flex items-center justify-center z-50">
                <div class="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
                <div class="relative bg-white rounded-2xl p-8 w-[600px] max-w-[90vw] shadow-2xl">
                    <h3 class="text-2xl font-bold text-gray-900 mb-6">导入手机号白名单</h3>
                    
                    <div class="mb-6">
                        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <div class="flex items-start space-x-3">
                                <div class="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <i class="fas fa-info-circle text-blue-600 text-sm"></i>
                                </div>
                                <div>
                                    <h4 class="text-sm font-semibold text-blue-800 mb-1">使用说明</h4>
                                    <ul class="text-sm text-blue-700 space-y-1">
                                        <li>• 请先下载Excel模板，按照模板格式填写手机号</li>
                                        <li>• 手机号填写在第一列，从第一行开始</li>
                                        <li>• 支持.xlsx和.xls格式的Excel文件</li>
                                        <li>• 系统会自动验证手机号格式并去重</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        
                        <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors duration-200">
                            <div v-if="!selectedFile" class="space-y-4">
                                <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                    <i class="fas fa-file-excel text-gray-400 text-2xl"></i>
                                </div>
                                <div>
                                    <p class="text-lg font-medium text-gray-900 mb-2">选择Excel文件</p>
                                    <p class="text-sm text-gray-500 mb-4">点击选择文件或拖拽文件到此区域</p>
                                    <input 
                                        ref="fileInput"
                                        type="file"
                                        accept=".xlsx,.xls"
                                        @change="handleFileSelect"
                                        class="hidden"
                                    />
                                    <button 
                                        @click="$refs.fileInput.click()"
                                        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                                    >
                                        选择文件
                                    </button>
                                </div>
                            </div>
                            
                            <div v-else class="space-y-4">
                                <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                    <i class="fas fa-check text-green-600 text-2xl"></i>
                                </div>
                                <div>
                                    <p class="text-lg font-medium text-gray-900 mb-2">已选择文件</p>
                                    <p class="text-sm text-gray-600 mb-2">{{ selectedFile.name }}</p>
                                    <p class="text-xs text-gray-500">文件大小: {{ formatFileSize(selectedFile.size) }}</p>
                                </div>
                                <button 
                                    @click="clearSelectedFile"
                                    class="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
                                >
                                    重新选择
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    <div class="flex space-x-4">
                        <button 
                            @click="importExcel"
                            :disabled="!selectedFile || isImporting"
                            class="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                        >
                            <i v-if="isImporting" class="fas fa-spinner fa-spin"></i>
                            <i v-else class="fas fa-upload"></i>
                            <span>{{ isImporting ? '导入中...' : '开始导入' }}</span>
                        </button>
                        <button 
                            @click="closeExcelImportModal"
                            class="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                        >
                            取消
                        </button>
                    </div>
                    
                    <button 
                        @click="closeExcelImportModal"
                        class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
                    >
                        <i class="fas fa-times text-gray-600"></i>
                    </button>
                </div>
            </div>

            <!-- 导入结果弹窗 -->
            <div v-if="showImportResultModal" class="fixed inset-0 flex items-center justify-center z-50">
                <div class="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
                <div class="relative bg-white rounded-2xl p-8 w-[600px] max-w-[90vw] shadow-2xl">
                    <h3 class="text-2xl font-bold text-gray-900 mb-6">导入结果</h3>
                    
                    <div class="space-y-4">
                        <div v-for="(result, index) in importResults" :key="index" 
                             class="p-3 rounded-lg"
                             :class="result.includes('成功') ? 'bg-green-50 text-green-800' : 
                                     result.includes('失败') ? 'bg-red-50 text-red-800' : 
                                     'bg-blue-50 text-blue-800'">
                            <p class="text-sm">{{ result }}</p>
                        </div>
                    </div>
                    
                    <div class="flex justify-end mt-6">
                        <button 
                            @click="closeImportResultModal"
                            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                        >
                            确定
                        </button>
                    </div>
                    
                    <button 
                        @click="closeImportResultModal"
                        class="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
                    >
                        <i class="fas fa-times text-gray-600"></i>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import apiConfig from '@/config/api'

export default {
    name: 'UserManagement',
    data() {
        return {
            currentUser: {
                id: null,
                displayName: '',
                loginAccount: '',
                phone: '',
                role: 'user',
                status: 1,
                createTime: '',
                lastLoginTime: ''
            },
            users: [],
            searchQuery: '',
            statusFilter: '',
            roleFilter: '',
            currentPage: 1,
            pageSize: 10,
            totalUsers: 0,
            showEditModal: false,
            showAddUserModal: false,
            showPermissionModal: false,
            showResetPasswordModal: false,
            isResettingPassword: false,
            showExcelImportModal: false,
            showImportResultModal: false,
            isImporting: false,
            selectedFile: null,
            importResults: [],
            editingUser: {},
            permissionUser: {
                id: null,
                displayName: '',
                loginAccount: '',
                knowledgeAccess: false,
                userManagementAccess: false
            },
            resetPasswordUser: {
                id: null,
                displayName: '',
                loginAccount: ''
            },
            newUser: {
                displayName: '',
                loginAccount: '',
                password: '',
                phone: '',
                role: 'user'
            }
        };
    },
    computed: {
        filteredUsers() {
            let filtered = this.users;
            
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter(user => 
                    user.displayName.toLowerCase().includes(query) ||
                    user.loginAccount.toLowerCase().includes(query) ||
                    user.phone.includes(query)
                );
            }
            
            if (this.statusFilter !== '') {
                filtered = filtered.filter(user => user.status.toString() === this.statusFilter);
            }
            
            if (this.roleFilter !== '') {
                filtered = filtered.filter(user => user.role === this.roleFilter);
            }
            
            return filtered;
        },
        totalPages() {
            return Math.ceil(this.totalUsers / this.pageSize);
        }
    },
    async mounted() {
        await this.loadCurrentUser();
        if (this.currentUser.role === 'admin' || this.currentUser.role === 'user_manager') {
            await this.loadUsers();
        }
    },
    methods: {
        async loadCurrentUser() {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    this.$router.push('/login');
                    return;
                }
                
                // 使用完整URL，确保指向正确的服务器
                const url = `${apiConfig.baseURL}/api/auth/current-user`;
                console.log('调用当前用户API:', url);
                
                const response = await fetch(url, {
                    headers: {
                        'Authorization': token
                    }
                });
                
                console.log('当前用户API响应状态:', response.status);
                
                if (response.ok) {
                    const result = await response.json();
                    console.log('当前用户API响应数据:', result);
                    if (result.code === 200) {
                        this.currentUser = result.data;
                        console.log('设置当前用户数据:', this.currentUser);
                    } else {
                        console.error('当前用户API返回失败:', result.message);
                    }
                } else {
                    console.error('当前用户API调用失败，状态码:', response.status);
                }
            } catch (error) {
                console.error('加载当前用户信息失败:', error);
            }
        },
        
        async loadUsers() {
            try {
                const token = localStorage.getItem('token');
                // Spring Boot分页从0开始，前端从1开始，需要减1
                const page = this.currentPage - 1;
                // 使用完整URL，确保指向正确的服务器
                const url = `${apiConfig.baseURL}/api/users?page=${page}&size=${this.pageSize}`;
                console.log('调用用户列表API:', url);
                
                const response = await fetch(url, {
                    headers: {
                        'Authorization': token
                    }
                });
                
                console.log('API响应状态:', response.status);
                
                if (response.ok) {
                    const result = await response.json();
                    console.log('API响应数据:', result);
                    if (result.code === 200) {
                        this.users = result.data.content;
                        this.totalUsers = result.data.totalElements;
                        console.log('设置用户数据:', this.users);
                        console.log('总用户数:', this.totalUsers);
                    } else {
                        console.error('API返回失败:', result.message);
                    }
                } else {
                    console.error('API调用失败，状态码:', response.status);
                }
            } catch (error) {
                console.error('加载用户列表失败:', error);
            }
        },
        
        // 获取角色显示名称
        getRoleDisplayName(role) {
            switch (role) {
                case 'admin':
                    return '管理员';
                case 'user_manager':
                    return '用户管理员';
                case 'user':
                    return '普通用户';
                default:
                    return '未知角色';
            }
        },
        
        // 获取角色样式类
        getRoleClass(role) {
            switch (role) {
                case 'admin':
                    return 'bg-red-100 text-red-800';
                case 'user_manager':
                    return 'bg-blue-100 text-blue-800';
                case 'user':
                    return 'bg-green-100 text-green-800';
                default:
                    return 'bg-gray-100 text-gray-800';
            }
        },
        
        editCurrentUser() {
            this.editingUser = { ...this.currentUser };
            this.showEditModal = true;
        },
        
        editUser(user) {
            this.editingUser = { ...user };
            this.showEditModal = true;
        },
        
        async saveUser() {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${apiConfig.baseURL}/api/users/${this.editingUser.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token
                    },
                    body: JSON.stringify(this.editingUser)
                });
                
                if (response.ok) {
                    const result = await response.json();
                    if (result.code === 200) {
                        // 如果是当前用户，更新本地信息
                        if (this.editingUser.id === this.currentUser.id) {
                            this.currentUser = { ...this.editingUser };
                            localStorage.setItem('displayName', this.currentUser.displayName);
                        }
                        
                        // 刷新用户列表
                        await this.loadUsers();
                        this.closeEditModal();
                        alert('用户信息更新成功');
                    }
                }
            } catch (error) {
                console.error('保存用户信息失败:', error);
                alert('保存失败，请重试');
            }
        },
        
        async addUser() {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${apiConfig.baseURL}/api/users`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token
                    },
                    body: JSON.stringify(this.newUser)
                });
                
                if (response.ok) {
                    const result = await response.json();
                    if (result.code === 200) {
                        await this.loadUsers();
                        this.closeAddUserModal();
                        this.resetNewUser();
                        alert('用户添加成功');
                    }
                }
            } catch (error) {
                console.error('添加用户失败:', error);
                alert('添加失败，请重试');
            }
        },
        
        async toggleUserStatus(user) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${apiConfig.baseURL}/api/users/${user.id}/toggle-status`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': token
                    }
                });
                
                if (response.ok) {
                    const result = await response.json();
                    if (result.code === 200) {
                        await this.loadUsers();
                        alert(`用户状态已${user.status === 1 ? '禁用' : '启用'}`);
                    }
                }
            } catch (error) {
                console.error('切换用户状态失败:', error);
                alert('操作失败，请重试');
            }
        },
        
        async deleteUser(user) {
            if (!confirm(`确定要删除用户 "${user.displayName}" 吗？此操作不可恢复。`)) {
                return;
            }
            
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${apiConfig.baseURL}/api/users/${user.id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': token
                    }
                });
                
                if (response.ok) {
                    const result = await response.json();
                    if (result.code === 200) {
                        await this.loadUsers();
                        alert('用户删除成功');
                    }
                }
            } catch (error) {
                console.error('删除用户失败:', error);
                alert('删除失败，请重试');
            }
        },
        
        closeEditModal() {
            this.showEditModal = false;
            this.editingUser = {};
        },
        
        closeAddUserModal() {
            this.showAddUserModal = false;
            this.resetNewUser();
        },
        
        resetNewUser() {
            this.newUser = {
                displayName: '',
                loginAccount: '',
                password: '',
                phone: '',
                role: 'user'
            };
        },
        
        prevPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
                this.loadUsers();
            }
        },
        
        nextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
                this.loadUsers();
            }
        },
        
        formatDate(dateString) {
            if (!dateString) return '未知';
            return new Date(dateString).toLocaleDateString('zh-CN');
        },
        
        formatDateTime(dateString) {
            if (!dateString) return null;
            try {
                const date = new Date(dateString);
                return date.toLocaleString('zh-CN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                });
            } catch (error) {
                console.error('日期格式化错误:', error);
                return dateString;
            }
        },
        
        goBackToChat() {
            this.$router.push('/chat');
        },
        
        openPermissionModal(user) {
            this.permissionUser = {
                id: user.id,
                displayName: user.displayName,
                loginAccount: user.loginAccount,
                knowledgeAccess: user.knowledgeAccess || false,
                userManagementAccess: user.userManagementAccess || false
            };
            this.showPermissionModal = true;
        },
        
        closePermissionModal() {
            this.showPermissionModal = false;
            this.permissionUser = {
                id: null,
                displayName: '',
                loginAccount: '',
                knowledgeAccess: false,
                userManagementAccess: false
            };
        },
        
        async savePermissions() {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${apiConfig.baseURL}/api/users/${this.permissionUser.id}/permissions`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token
                    },
                    body: JSON.stringify({
                        knowledgeAccess: this.permissionUser.knowledgeAccess,
                        userManagementAccess: this.permissionUser.userManagementAccess
                    })
                });
                
                if (response.ok) {
                    const result = await response.json();
                    if (result.code === 200) {
                        // 更新用户列表中的权限信息
                        const userIndex = this.users.findIndex(user => user.id === this.permissionUser.id);
                        if (userIndex !== -1) {
                            this.users[userIndex].knowledgeAccess = this.permissionUser.knowledgeAccess;
                            this.users[userIndex].userManagementAccess = this.permissionUser.userManagementAccess;
                        }
                        
                        this.closePermissionModal();
                        alert('用户权限更新成功');
                    } else {
                        alert('权限更新失败: ' + result.message);
                    }
                } else {
                    alert('权限更新失败，请重试');
                }
            } catch (error) {
                console.error('保存用户权限失败:', error);
                alert('保存失败，请重试');
            }
        },
        
        openResetPasswordModal(user) {
            this.resetPasswordUser = {
                id: user.id,
                displayName: user.displayName,
                loginAccount: user.loginAccount
            };
            this.showResetPasswordModal = true;
        },
        
        closeResetPasswordModal() {
            this.showResetPasswordModal = false;
            this.isResettingPassword = false;
            this.resetPasswordUser = {
                id: null,
                displayName: '',
                loginAccount: ''
            };
        },
        
        async confirmResetPassword() {
            this.isResettingPassword = true;
            
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${apiConfig.baseURL}/api/users/${this.resetPasswordUser.id}/reset-password`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': token
                    }
                });
                
                if (response.ok) {
                    const result = await response.json();
                    if (result.code === 200) {
                        this.closeResetPasswordModal();
                        alert('密码重置成功！新密码为：123456');
                    } else {
                        alert('密码重置失败: ' + result.message);
                    }
                } else {
                    alert('密码重置失败，请重试');
                }
            } catch (error) {
                console.error('重置用户密码失败:', error);
                alert('重置失败，请重试');
            } finally {
                this.isResettingPassword = false;
            }
        },
        
        async downloadExcelTemplate() {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${apiConfig.baseURL}/api/users/excel-template`, {
                    headers: {
                        'Authorization': token
                    }
                });
                
                if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'phone_template.xlsx';
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                } else {
                    alert('下载模板失败，请重试');
                }
            } catch (error) {
                console.error('下载模板失败:', error);
                alert('下载失败，请重试');
            }
        },
        
        handleFileSelect(event) {
            const file = event.target.files[0];
            if (file) {
                // 检查文件类型
                const fileName = file.name;
                if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
                    alert('请选择Excel文件（.xlsx或.xls格式）');
                    return;
                }
                
                // 检查文件大小（限制为10MB）
                if (file.size > 10 * 1024 * 1024) {
                    alert('文件大小不能超过10MB');
                    return;
                }
                
                this.selectedFile = file;
            }
        },
        
        clearSelectedFile() {
            this.selectedFile = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },
        
        closeExcelImportModal() {
            this.showExcelImportModal = false;
            this.clearSelectedFile();
            this.isImporting = false;
        },
        
        closeImportResultModal() {
            this.showImportResultModal = false;
            this.importResults = [];
        },
        
        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        
        async importExcel() {
            if (!this.selectedFile) {
                alert('请先选择要导入的Excel文件');
                return;
            }
            
            this.isImporting = true;
            
            try {
                const token = localStorage.getItem('token');
                const formData = new FormData();
                formData.append('file', this.selectedFile);
                
                const response = await fetch(`${apiConfig.baseURL}/api/users/import-excel`, {
                    method: 'POST',
                    headers: {
                        'Authorization': token
                    },
                    body: formData
                });
                
                if (response.ok) {
                    const result = await response.json();
                    if (result.code === 200) {
                        this.importResults = result.data.results || [];
                        this.closeExcelImportModal();
                        this.showImportResultModal = true;
                    } else {
                        alert('导入失败: ' + result.message);
                    }
                } else {
                    alert('导入失败，请重试');
                }
            } catch (error) {
                console.error('导入Excel失败:', error);
                alert('导入失败，请重试');
            } finally {
                this.isImporting = false;
            }
        }
    }
};
</script>

<style scoped>
/* 可以添加一些自定义样式 */
</style>
